/**
 * Admin · Family Keepsakes
 *
 * Hidden gallery — one thumbnail per completed session on the public
 * `/spine/mum` and `/heart/dad` pages. Each thumbnail shows Nanna's or
 * Grandad's actual Cathedral / Heart bloom at the moment it peaked,
 * with the voice they picked and when.
 *
 * Route: /admin/family-keepsakes (admin only)
 */
import { useEffect, useState } from "react";
import { Loader2, Users, Heart, Sparkle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("reclaim_token")}` });

export default function AdminFamilyKeepsakes() {
  const [items, setItems] = useState(null);
  const [stats, setStats] = useState(null);
  const [err, setErr] = useState(null);
  const [imgUrls, setImgUrls] = useState({});   // filename → blob:URL cache

  useEffect(() => {
    (async () => {
      try {
        const [k, s] = await Promise.all([
          fetch(`${API}/api/family-journey/keepsakes?limit=200`, { headers: auth() }).then((r) => r.json()),
          fetch(`${API}/api/family-journey/stats`,             { headers: auth() }).then((r) => r.json()),
        ]);
        setItems(k?.keepsakes || []);
        setStats(s || null);
      } catch (e) {
        setErr(String(e));
      }
    })();
  }, []);

  // Images are behind an auth-required endpoint, so we <img src> can't
  // use them directly. Fetch each as a blob with the token, then swap
  // in the object URL.
  useEffect(() => {
    if (!items) return;
    let cancelled = false;
    const load = async () => {
      const next = {};
      for (const it of items) {
        try {
          const r = await fetch(`${API}${it.path}`, { headers: auth() });
          if (!r.ok) continue;
          const blob = await r.blob();
          next[it.filename] = URL.createObjectURL(blob);
        } catch (_) { /* skip */ }
      }
      if (!cancelled) setImgUrls(next);
    };
    load();
    return () => {
      cancelled = true;
      Object.values(imgUrls).forEach(URL.revokeObjectURL);
    };
  }, [items]);

  const formatTime = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
    } catch { return iso; }
  };

  return (
    <div className="min-h-screen bg-neutral-950 text-white/90 px-6 py-10" data-testid="admin-family-keepsakes">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <h1
            className="text-4xl sm:text-5xl mb-2"
            style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', color: "#e6c77a" }}
          >
            Family Keepsakes
          </h1>
          <p className="text-white/50 italic text-sm">
            Every session Nanna &amp; Grandad have completed — a private gallery of their light.
          </p>
        </div>

        {/* Voice-pick summary */}
        {stats && stats.by_voice && stats.by_voice.length > 0 && (
          <div className="mb-10 grid grid-cols-1 md:grid-cols-2 gap-4">
            {["nanna", "grandad"].map((recipient) => {
              const rows = stats.by_voice.filter((r) => r.recipient === recipient);
              if (rows.length === 0) return null;
              const total = rows.reduce((sum, r) => sum + r.count, 0);
              return (
                <div key={recipient}
                     className="rounded-2xl border border-white/10 bg-white/[0.02] p-5"
                     data-testid={`kepsake-summary-${recipient}`}
                >
                  <div className="flex items-center gap-2 mb-3">
                    {recipient === "nanna" ? <Sparkle className="w-4 h-4 text-[#e6c77a]"/> : <Heart className="w-4 h-4 text-[#e6a189]"/>}
                    <span className="uppercase tracking-widest text-xs text-white/60">
                      {recipient} · {total} session{total === 1 ? "" : "s"}
                    </span>
                  </div>
                  <ul className="space-y-1.5 text-sm">
                    {rows.slice(0, 8).map((r) => (
                      <li key={r.speaker_name} className="flex justify-between text-white/70">
                        <span>{r.speaker_name || "—"}</span>
                        <span className="text-white/40">
                          {r.count} × &nbsp;·&nbsp; last {formatTime(r.last_picked_at)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        )}

        {/* Gallery */}
        {err && <div className="text-red-400/80 text-sm mb-6">Error: {err}</div>}
        {items === null && (
          <div className="flex items-center gap-2 text-white/50"><Loader2 className="w-4 h-4 animate-spin"/>Loading…</div>
        )}
        {items && items.length === 0 && (
          <div className="text-white/50 italic flex items-center gap-2"><Users className="w-4 h-4"/>No keepsakes yet — the first one will land here the moment Nanna or Grandad completes a session.</div>
        )}
        {items && items.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {items.map((it) => (
              <div
                key={it.filename}
                className="rounded-2xl overflow-hidden border border-white/10 bg-white/[0.02]"
                data-testid={`keepsake-${it.filename}`}
              >
                <div className="aspect-video bg-black overflow-hidden flex items-center justify-center">
                  {imgUrls[it.filename]
                    ? <img src={imgUrls[it.filename]} alt="keepsake" className="w-full h-full object-cover"/>
                    : <Loader2 className="w-4 h-4 animate-spin text-white/40"/>}
                </div>
                <div className="p-4">
                  <div className="text-sm text-white/90 mb-0.5">
                    {it.recipient === "nanna" ? "Nanna" : it.recipient === "grandad" ? "Grandad" : it.recipient}
                    <span className="text-white/40"> · {it.page === "spine" ? "Spinal Ease" : "Heart Flow"}</span>
                  </div>
                  <div className="text-xs text-white/50">
                    With {it.speaker_name || "—"}  ·  {formatTime(it.captured_at)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
